import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toaster';
import { WorkgroupsUploadDropZone } from '@/features/workgroups/components/workgroups-upload-drop-zone';
import {
  isConflictCopyFileMetadata,
  isWorkGroupQuotaExceededError,
  useUploadWorkGroupFile,
  workgroupsMutationErrorMessage,
} from '@/hooks/use-workgroups';

interface UploadFileDialogProps {
  workGroupId: string;
  folderId?: string;
  privateAccessReason?: string;
}

function UploadProgressBar({
  percent,
  isUploading,
}: {
  percent: number | null;
  isUploading: boolean;
}) {
  if (!isUploading) return null;

  const indeterminate = percent === null;
  const value = percent ?? 0;

  return (
    <div className="space-y-1" aria-live="polite">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>Uploading…</span>
        <span>{indeterminate ? 'In progress' : `${value}%`}</span>
      </div>
      {indeterminate ? (
        <div
          role="progressbar"
          aria-label="Upload in progress"
          className="h-2 w-full overflow-hidden rounded-full bg-muted"
        >
          <div className="h-full w-1/3 animate-pulse rounded-full bg-primary" />
        </div>
      ) : (
        <progress
          className="h-2 w-full overflow-hidden rounded-full accent-primary"
          max={100}
          value={value}
          aria-label="Upload progress"
        />
      )}
    </div>
  );
}

export function UploadFileDialog({
  workGroupId,
  folderId,
  privateAccessReason,
}: UploadFileDialogProps) {
  const [open, setOpen] = useState(false);
  const [quotaExceededOpen, setQuotaExceededOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const mutation = useUploadWorkGroupFile();

  const resetForm = useCallback(() => {
    setSelectedFile(null);
    setUploadProgress(null);
  }, []);

  const handleUpload = () => {
    if (!selectedFile) {
      toast({ title: 'Choose a file first', variant: 'destructive' });
      return;
    }
    setUploadProgress(null);
    mutation.mutate(
      {
        workGroupId,
        file: selectedFile,
        folderId,
        privateAccessReason,
        onUploadProgress: setUploadProgress,
      },
      {
        onSuccess: (uploadedFile) => {
          toast({ title: 'File uploaded' });
          if (isConflictCopyFileMetadata(uploadedFile)) {
            toast({
              title: 'Conflict copy created',
              description:
                'A file with this name already existed, so WeWorkGroups saved this upload as a conflict copy.',
            });
          }
          setOpen(false);
          resetForm();
        },
        onError: (err) => {
          setUploadProgress(null);
          if (isWorkGroupQuotaExceededError(err)) {
            setQuotaExceededOpen(true);
            return;
          }
          toast({ title: workgroupsMutationErrorMessage(err), variant: 'destructive' });
        },
      },
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (mutation.isPending) return;
          setOpen(next);
          if (!next) resetForm();
        }}
      >
        <DialogTrigger asChild>
          <Button size="sm">Upload file</Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload file</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <WorkgroupsUploadDropZone
              inputId="wg-upload-file"
              selectedFile={selectedFile}
              disabled={mutation.isPending}
              onFileSelected={setSelectedFile}
              onValidationError={(message) => {
                toast({ title: message, variant: 'destructive' });
              }}
              onMultipleFilesDropped={() => {
                toast({
                  title: 'Only one file can be uploaded at a time. Using the first file.',
                });
              }}
            />
            <UploadProgressBar percent={uploadProgress} isUploading={mutation.isPending} />
          </div>
          <DialogFooter>
            <Button
              type="button"
              disabled={!selectedFile || mutation.isPending}
              onClick={handleUpload}
            >
              {mutation.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={quotaExceededOpen} onOpenChange={setQuotaExceededOpen}>
        <DialogContent data-testid="wg-quota-exceeded-dialog">
          <DialogHeader>
            <DialogTitle>Storage quota full</DialogTitle>
            <DialogDescription>
              This work group has no free storage left. Uploads are blocked until space is freed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>Delete files you no longer need or permanently remove items from trash.</p>
            <p>Items in trash still count toward quota until they are permanently deleted.</p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setQuotaExceededOpen(false)}>
              Close
            </Button>
            <Button type="button" asChild>
              <Link to={`/workgroups/${workGroupId}/trash`} onClick={() => setQuotaExceededOpen(false)}>
                Go to trash
              </Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
